import React from 'react'
import _cx from 'classnames/bind'
import { connect } from 'react-redux'
import configment from '@adc/configment'
import {
  Grid,
  Text,
  ChatLink,
  Button,
  Icon,
  hooks,
  useApplicationContext,
  constants,
  chatUtils,
  CardContainer,
} from '@adc/ui-adc'
import type { primitives } from '@adc/blueprint'
import { Address } from 'common/components/address'
import { useListingContext } from '../../context/listing'
import { useAiPrompt } from '../../context/aiPrompt'
import SectionPanel from '../../components/sectionPanel'
import { getAiFaqMode } from '../faqsWithAi'
import cls from './index.scss'

const { ColorNames } = constants

const cx = _cx.bind(cls)
const EMAIL_NEWLINE = '%0D%0A'
const elmId = 'help_center'
const phoneElmId = `${elmId}_email_phone`

interface PhoneNumberProps {
  isREO: boolean
}
const PhoneNumber = ({ isREO }: PhoneNumberProps) => {
  if (isREO) {
    return (
      <a href='tel:8007936107' data-elm-id={phoneElmId}>
        (800) 793-6107
      </a>
    )
  } else {
    return (
      <a href='tel:8002802832' data-elm-id={phoneElmId}>
        (800) 280-2832
      </a>
    )
  }
}

/**
 * Check if current time is within business hours (Mon-Fri, 5AM-5PM Pacific Time).
 * Uses Intl.DateTimeFormat to correctly handle DST transitions.
 */
const isBusinessHours = (): boolean => {
  const now = new Date()
  const ptFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    weekday: 'short',
    hour: 'numeric',
    hour12: false,
  })
  const parts = ptFormatter.formatToParts(now)
  const weekday = parts.find(p => p.type === 'weekday')?.value
  const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10)

  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']
  if (!weekday || !weekdays.includes(weekday)) return false
  return hour >= 5 && hour < 17
}

interface HelpCenterProps {
  user: primitives.AuctionUser
}

const HelpCenter = ({ user }: HelpCenterProps) => {
  const { listing } = useListingContext()
  const { onClickAiPrompt } = useAiPrompt()
  const isMobile = hooks.useIsMobileHook()
  const isREO = listing.auction?.is_online || false
  const emailSubject = `Question about ${Address.format(listing)}`
  const emailBody = `I have a question about this property:${EMAIL_NEWLINE}${
    String(configment.get('AUCTIONCOM_HOST')) +
    String(listing.listing_page_path)
  }${EMAIL_NEWLINE}`

  const { featureFlags } = useApplicationContext()
  const aiFaqMode = getAiFaqMode(featureFlags)
  const isAiEnabled = aiFaqMode === 'chat'
  const duringBusinessHours = isBusinessHours()

  // ─── Non-AI Mode (classic: Chat / Email / Talk) ───
  if (!isAiEnabled) {
    const classicDesktopView = (
      <SectionPanel elmId='help_center'>
        <Grid>
          <Grid.Row>
            <Grid.Column computer={16} tablet={16}>
              <Text.Header
                className={cx('u-text-center')}
                // @ts-ignore fix types in DS, export enum if possible
                weight='medium'
                color='grayDark'
                level={2}
              >
                Need Help?
              </Text.Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row centered>
            <Grid.Column computer={5} tablet={5}>
              <div className={cx('u-text-center', 'u-sm-mb-6')}>
                <div className={cx('u-mb-2', 'icon-container')}>
                  <Icon
                    name='messages'
                    iconStyle='regular'
                    color={ColorNames.AdcLightGreen}
                    style={{ fontSize: '40px', margin: 0, lineHeight: 1 }}
                  />
                </div>
                <Text.Header level={3} className={cx('header')}>
                  Chat with us
                </Text.Header>
                <Text>We're online</Text>
                <Text>Mon-Fri</Text>
                <Text>5am-5pm PT</Text>
                <Text>
                  <a data-elm-id={`${elmId}_chat_now`}>
                    <ChatLink user={user} chatText='Chat Now' />
                  </a>
                </Text>
              </div>
            </Grid.Column>
            <Grid.Column computer={5} tablet={5}>
              <div className={cx('u-text-center', 'u-sm-mb-6')}>
                <div className='u-mb-2'>
                  <Icon
                    name='envelope'
                    iconStyle='regular'
                    color={ColorNames.AdcLightBlue}
                    style={{ fontSize: '40px', margin: 0, lineHeight: 1 }}
                  />
                </div>
                <Text.Header level={3} className={cx('header')}>
                  Email us
                </Text.Header>
                <Text>For any questions,</Text>
                <Text>please send us a message</Text>
                <Text>
                  <a
                    data-elm-id={`${elmId}_email`}
                    href={`mailto:customerservice@auction.com?subject=${emailSubject}&body=${emailBody}`}
                  >
                    Get Started
                  </a>
                </Text>
              </div>
            </Grid.Column>
            {!aiFaqMode && (
              <Grid.Column computer={5} tablet={5}>
                <div className={cx('u-text-center', 'u-sm-mb-6')}>
                  <div className='u-mb-2'>
                    <Icon
                      name='user-headset'
                      iconStyle='regular'
                      color={ColorNames.AdcDarkGreen}
                      style={{ fontSize: '40px', margin: 0, lineHeight: 1 }}
                    />
                  </div>
                  <Text.Header level={3} className={cx('header')}>
                    Talk to us
                  </Text.Header>
                  <Text>
                    <PhoneNumber isREO={isREO} />
                  </Text>
                  <Text>Mon-Fri</Text>
                  <Text>5am-5pm PT</Text>
                </div>
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
      </SectionPanel>
    )

    const classicMobileView = (
      <SectionPanel elmId='help_center'>
        <Grid>
          <Grid.Row>
            <Grid.Column mobile={16}>
              <Text
                weight='normal'
                size='huge'
                className={cx('contact-title')}
                color='grayDark'
              >
                Need Help?
              </Text>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column mobile={16}>
              <div className={cx('contact-row')}>
                <div className={cx('contact-image')}>
                  <Icon
                    name='messages'
                    iconStyle='regular'
                    color={ColorNames.AdcLightGreen}
                    style={{ fontSize: '40px', margin: 0, lineHeight: 1 }}
                  />
                </div>
                <div>
                  <Text size='small' className={cx('mobile-header')}>
                    Chat with us.
                  </Text>
                  <Text size='tiny' color='gray'>
                    We're online Mon-Fri 5am-5pm PT
                  </Text>
                  <Text size='tiny'>
                    <a data-elm-id={`${elmId}_chat_now`}>
                      <ChatLink user={user} chatText='Chat Now' />
                    </a>
                  </Text>
                </div>
              </div>
            </Grid.Column>
            <Grid.Column mobile={16}>
              <div className={cx('contact-row')}>
                <div className={cx('contact-image')}>
                  <Icon
                    name='envelope'
                    iconStyle='regular'
                    color={ColorNames.AdcLightBlue}
                    style={{ fontSize: '40px', margin: 0, lineHeight: 1 }}
                  />
                </div>
                <div>
                  <Text size='small' className={cx('mobile-header')}>
                    Email us.
                  </Text>
                  <Text size='tiny' color='gray'>
                    For any questions, please send us a message
                  </Text>
                  <Text size='tiny'>
                    <a
                      data-elm-id={`${elmId}_email`}
                      href={`mailto:customerservice@auction.com?subject=${emailSubject}&body=${emailBody}`}
                    >
                      Get Started
                    </a>
                  </Text>
                </div>
              </div>
            </Grid.Column>
            {!aiFaqMode && (
              <Grid.Column mobile={16}>
                <div className={cx('contact-row')}>
                  <div className={cx('contact-image')}>
                    <Icon
                      name='user-headset'
                      iconStyle='regular'
                      color={ColorNames.AdcDarkGreen}
                      style={{ fontSize: '40px', margin: 0, lineHeight: 1 }}
                    />
                  </div>
                  <div>
                    <Text size='small' className={cx('mobile-header')}>
                      Talk to us.
                    </Text>
                    <Text color='gray' size='tiny'>
                      Mon - Fri 5am - 5pm PT
                    </Text>
                    <Text size='tiny'>
                      <PhoneNumber isREO={isREO} />
                    </Text>
                  </div>
                </div>
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
      </SectionPanel>
    )

    return isMobile ? classicMobileView : classicDesktopView
  }

  // ─── AI Mode (Chat with Us + Ask Auction.com cards) ───
  const chatButton = duringBusinessHours ? (
    <Button
      size='medium'
      className={cx('chat-now-button')}
      role='secondary'
      // @ts-ignore next
      color={ColorNames.Info}
      onClick={() => chatUtils.openChatPopup(user)}
      elmId={`${elmId}_chat_now`}
    >
      Chat Now
      <Icon
        className={cx('ask-ai-icon')}
        name='comment-arrow-up-right'
        iconStyle='solid'
        size='medium'
        color={ColorNames.Info}
      />
    </Button>
  ) : (
    <Button
      size='medium'
      className={cx('chat-now-button')}
      role='secondary'
      // @ts-ignore next
      color={ColorNames.Info}
      disabled
      elmId={`${elmId}_chat_now`}
    >
      Chat is Currently Offline
    </Button>
  )

  const askAiButton = (
    <Button
      size='medium'
      className={cx('chat-now-button')}
      onClick={() => onClickAiPrompt()}
      role='secondary'
      // @ts-ignore next
      color={ColorNames.Info}
      elmId={`${elmId}_ask_ai`}
    >
      Ask Us Something
      <Icon
        className={cx('ask-ai-icon')}
        name='comment-arrow-up-right'
        iconStyle='solid'
        size='medium'
        color={ColorNames.Info}
      />
    </Button>
  )

  const aiDesktopView = (
    <SectionPanel elmId='help_center'>
      <Text.Header
        className={cx('u-text-center')}
        // @ts-ignore fix types in DS, export enum if possible
        weight='medium'
        color='grayDark'
        level={2}
      >
        Need Help?
      </Text.Header>
      <div className={cx('ai-cards-row')}>
        <CardContainer
          // @ts-ignore type exists
          padding='medium'
          borderStyle='line'
          borderColor={constants.ColorNames.GrayLight}
          borderRadius='medium'
          className={cx('ai-card-container')}
        >
          <div className={cx('ai-card-content')}>
            <div className={cx('icon-container')}>
              <Icon
                name='messages'
                iconStyle='regular'
                color={ColorNames.AdcLightGreen}
                style={{ fontSize: '40px', margin: 0, lineHeight: 1 }}
              />
            </div>
            <Text.Header level={3} className={cx('header')}>
              Chat with Us.
            </Text.Header>
            <Text color='gray'>We're online Mon - Fri, 5am - 5pm PT</Text>
            <div className={cx('card-button-container')}>{chatButton}</div>
          </div>
        </CardContainer>
        <CardContainer
          // @ts-ignore type exists
          padding='medium'
          borderStyle='line'
          borderColor={constants.ColorNames.GrayLight}
          borderRadius='medium'
          className={cx('ai-card-container')}
        >
          <div className={cx('ai-card-content')}>
            <div className={cx('icon-container')}>
              <Icon
                name='sparkles'
                iconStyle='regular'
                color={ColorNames.AdcLightBlue}
                style={{ fontSize: '40px', margin: 0, lineHeight: 1 }}
              />
            </div>
            <Text.Header level={3} className={cx('header')}>
              Ask Auction.com
            </Text.Header>
            <Text color='gray'>
              Questions about this property? Chat with our AI Assistant for the
              fastest response.
            </Text>
            <div className={cx('card-button-container')}>{askAiButton}</div>
          </div>
        </CardContainer>
      </div>
    </SectionPanel>
  )

  const aiMobileView = (
    <SectionPanel elmId='help_center'>
      <Grid>
        <Grid.Row>
          <Grid.Column mobile={16}>
            <Text
              weight='normal'
              size='huge'
              className={cx('contact-title')}
              color='grayDark'
            >
              Need Help?
            </Text>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column mobile={16}>
            <div className={cx('ai-mobile-card-wrapper')}>
              <CardContainer
                // @ts-ignore type exists
                padding='medium'
                borderStyle='line'
                borderColor={constants.ColorNames.GrayLight}
                borderRadius='medium'
              >
                <div className={cx('ai-mobile-card')}>
                  <div className={cx('ai-mobile-card-header')}>
                    <div className={cx('contact-image')}>
                      <Icon
                        name='messages'
                        iconStyle='regular'
                        color={ColorNames.AdcLightGreen}
                        style={{ fontSize: '40px' }}
                      />
                    </div>
                    <div>
                      <Text
                        size='small'
                        weight='heavy'
                        className={cx('mobile-header')}
                      >
                        Chat with Us.
                      </Text>
                      <Text size='tiny' color='gray'>
                        We're online Mon - Fri, 5am - 5pm PT
                      </Text>
                    </div>
                  </div>
                  <div className={cx('card-button-container')}>
                    {chatButton}
                  </div>
                </div>
              </CardContainer>
            </div>
          </Grid.Column>
          <Grid.Column mobile={16}>
            <div className={cx('ai-mobile-card-wrapper')}>
              <CardContainer
                // @ts-ignore type exists
                padding='medium'
                borderStyle='line'
                borderColor={constants.ColorNames.GrayLight}
                borderRadius='medium'
              >
                <div className={cx('ai-mobile-card')}>
                  <div className={cx('ai-mobile-card-header')}>
                    <div className={cx('contact-image')}>
                      <Icon
                        name='sparkles'
                        iconStyle='regular'
                        color={ColorNames.AdcLightBlue}
                        style={{ fontSize: '40px' }}
                      />
                    </div>
                    <div>
                      <Text
                        size='small'
                        weight='heavy'
                        className={cx('mobile-header')}
                      >
                        Ask Auction.com
                      </Text>
                      <Text size='tiny' color='gray'>
                        Questions about this property? Chat with our AI
                        Assistant for the fastest response.
                      </Text>
                    </div>
                  </div>
                  <div className={cx('card-button-container')}>
                    {askAiButton}
                  </div>
                </div>
              </CardContainer>
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </SectionPanel>
  )

  return isMobile ? aiMobileView : aiDesktopView
}

const mapStateToProps = (store: any) => ({
  user: store.user,
})

export default connect(mapStateToProps)(HelpCenter)
